import { FC, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import * as z from 'zod';
import toast from 'react-hot-toast';

import { useAuth } from '@hooks/Auth';

import InputText from '@components/elements/InputText';
import ButtonMighty from '@components/elements/ButtonMighty';

import './styles.css';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

const FORM_SCHEMA = z.object({
  password: z.string().min(6).max(99),
  newPassword: z.string().min(6).max(99),
});

type FormInput = z.infer<typeof FORM_SCHEMA>;

const EditAccountPage: FC = () => {
  const history = useHistory<any>();
  const { changePassword } = useAuth();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<FormInput>({
    resolver: zodResolver(FORM_SCHEMA),
  });

  const onSubmit = useCallback(
    async (data: FormInput) => {
      try {
        const res = changePassword(data.password, data.newPassword);

        await toast.promise(res, {
          loading: 'Updating new password...',
          success: 'Successfully updated password',
          error: (err) => {
            if (err.code === 'NotAuthorizedException')
              return 'Current password invalid';
            return `${err.message}`;
          },
        });

        await history.push('/account');
      } catch (err: any) {
        console.error(err);
      }
    },
    [changePassword, history]
  );

  const cancel = useCallback(async () => {
    history.replace('/account/edit');
  }, [history]);

  const labelCn = 'font-bold text-lg mb-1';

  return (
    <div className="px-4 pb-20 account-edit-background pt-14">
      <div className="container mx-auto">
        <div className="mb-8 text-white">
          <h1 className="mb-2 text-4xl">Change password</h1>
          <p className="text-lg">Update your profile and details</p>
        </div>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="p-4 text-black bg-yellow"
        >
          <div className="space-y-4">
            <div>
              <h2 className={labelCn}>Current Password</h2>
              <InputText
                type="password"
                placeholder="••••••"
                {...register('password')}
              />
              {errors.password && (
                <p className="text-right text-red">
                  *{errors.password.message}
                </p>
              )}
            </div>
            <div>
              <h2 className={labelCn}>New Password</h2>
              <InputText
                type="password"
                placeholder="••••••"
                {...register('newPassword')}
              />
              {errors.newPassword && (
                <p className="text-right text-red">
                  *{errors.newPassword.message}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col items-center mt-8 mb-4 space-y-4">
            <button className="w-full unstyled-btn xl:w-auto" type="submit">
              <ButtonMighty
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
                color="#4400FF"
                text="Save new password"
                borderColor="#ff1dfc"
                margin="0"
                textColor="#fff"
              />
            </button>
            <div className="w-full xl:w-auto">
              <ButtonMighty
                type="button"
                color="#221f20"
                text="Cancel"
                borderColor="#000"
                margin="0"
                textColor="#fff"
                onClick={cancel}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAccountPage;
